<template>
  <v-container class="modern-master-view pb-12">
    
    <!-- Header -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-file-invoice</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Facturas Relbase</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Listado centralizado de DTEs emitidos vía Relbase</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <!-- Story 4.5: Nuevo Documento — Modo Generalizado -->
      <v-btn
        color="accent"
        class="font-weight-black mr-3"
        @click="openNewDocumentDialog"
      >
        <v-icon left small>fas fa-plus</v-icon>
        Nuevo Documento
      </v-btn>
      <v-btn color="primary" icon class="grey lighten-4" @click="fetch" :loading="loading">
        <v-icon small>fas fa-sync</v-icon>
      </v-btn>
    </v-row>

    <!-- Filters -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense align="center">
        <v-col cols="12" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Desde</div>
          <v-text-field
            v-model="filter.date_emited_after"
            outlined dense hide-details clearable type="date"
            class="modern-input"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="12" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Hasta</div>
          <v-text-field
            v-model="filter.date_emited_before"
            outlined dense hide-details clearable type="date"
            class="modern-input"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="12" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Tipo DTE</div>
          <v-select
            v-model="filter.doc_type"
            :items="docTypes"
            item-text="text"
            item-value="value"
            placeholder="Todos"
            outlined dense hide-details clearable
            class="modern-input"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="12" md="4">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Cliente</div>
          <v-autocomplete
            v-model="filter.customer"
            :items="customers"
            :loading="loadingCustomers"
            :search-input.sync="customerSearch"
            item-text="name"
            item-value="id"
            placeholder="Buscar cliente..."
            outlined dense hide-details clearable
            no-filter
            class="modern-input"
            @change="onFilterChange"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title class="body-2 font-weight-bold">{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle class="caption">{{ item.rut }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>
    </v-card>

    <!-- Table -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="headers"
        :items="documents"
        :options.sync="options"
        :server-items-length="totalDocuments"
        :loading="loading"
        class="modern-table"
        @update:options="fetch"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <!-- Top slot: contador + búsqueda -->
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom bg-lightgrey d-flex align-center">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">
              Documentos emitidos: {{ totalDocuments }}
            </span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              @keyup="searchTimeOut"
              placeholder="Buscar por folio o cliente..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details clearable
              :disabled="loading"
              class="modern-search-field"
            />
          </div>
        </template>

        <!-- Folio + tipo -->
        <template v-slot:item.folio="{ item }">
          <div class="d-flex align-center py-2">
            <span class="body-2 font-weight-black primary--text tabular-nums mr-2">#{{ item.folio }}</span>
            <v-chip x-small :color="docTypeColor(item.doc_type) + ' lighten-5'" :class="docTypeColor(item.doc_type) + '--text font-weight-black'" label>
              {{ item.doc_type_display }}
            </v-chip>
          </div>
        </template>

        <!-- Fecha -->
        <template v-slot:item.date_emited="{ item }">
          <span class="body-2 grey--text text--darken-3 font-weight-medium">
            {{ item.date_emited | moment('DD/MM/YYYY') }}
          </span>
        </template>

        <!-- Fecha de Vencimiento (solo facturas) — Story 4.5 -->
        <template v-slot:item.end_date="{ item }">
          <span v-if="item.doc_type === 33 && item.end_date" class="body-2 grey--text text--darken-3 font-weight-medium">
            {{ item.end_date | moment('DD/MM/YYYY') }}
          </span>
          <span v-else class="caption grey--text opacity-40">—</span>
        </template>

        <!-- Cliente -->
        <template v-slot:item.customer_name="{ item }">
          <span class="body-2 font-weight-bold grey--text text--darken-4">
            {{ item.customer_name || '—' }}
          </span>
        </template>

        <!-- Total bruto -->
        <template v-slot:item.total_bruto="{ item }">
          <span class="body-1 font-weight-black primary--text tabular-nums tracking-tighter">
            {{ item.total_bruto | moneda }}
          </span>
        </template>

        <!-- OT Origen -->
        <template v-slot:item.work_order_id="{ item }">
          <show-work-order v-if="item.work_order_id" :work_order_id="item.work_order_id">
            <span class="body-2 font-weight-black primary--text tabular-nums">#{{ item.work_order_id }}</span>
          </show-work-order>
          <span v-else class="caption grey--text opacity-40">—</span>
        </template>

        <!-- Acciones -->
        <template v-slot:item.actions="{ item }">
          <v-menu bottom left offset-y transition="slide-y-transition" content-class="modern-card">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" class="grey lighten-4" @click.stop>
                <v-icon small color="grey darken-2">fas fa-ellipsis-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense class="py-1">
              <v-list-item @click="openDetail(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="primary">fas fa-eye</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">Ver detalle en Relbase</v-list-item-title>
              </v-list-item>
              <!-- Story 4.3: Nota de Crédito (solo aplica a Facturas y Guías) -->
              <v-list-item v-if="item.doc_type !== 61" @click="openNCDialog(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="warning">fas fa-file-invoice</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">Emitir Nota de Crédito</v-list-item-title>
              </v-list-item>
              <!-- Story 4.3: Anular -->
              <v-list-item @click="confirmAnular(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="error">fas fa-ban</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium error--text">Anular Documento</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialog detalle DTE -->
    <relbase-dte-detail-dialog v-model="showDetail" :doc="selectedDoc" />

    <!-- Story 4.3: Confirm anulación -->
    <confirm
      v-model="showAnularConfirm"
      :message="`¿Anular el documento Folio #${anularDoc && anularDoc.folio}? Esta acción es irreversible en Relbase.`"
      @ok="executeAnular"
      @cancel="showAnularConfirm = false"
    />

    <!-- Story 4.3: Diálogo de emisión de Nota de Crédito -->
    <relbase-issuance-dialog
      v-if="showNCDialog"
      v-model="showNCDialog"
      :doc-type="61"
      :reference-doc="ncReferenceDoc"
      @issued="onNCIssued"
    />

    <!-- Story 4.5: Nuevo Documento — Modo Generalizado (sin OT) -->
    <relbase-issuance-dialog
      v-if="showNewDocumentDialog"
      v-model="showNewDocumentDialog"
      :ot-id="null"
      :doc-type="newDocType"
      @issued="onNewDocumentIssued"
    />
  </v-container>
</template>

<script>
import { fetchRelbaseDocuments, anularRelbaseDTE } from '@/api/relbase'
import { fetchCustomers } from '@/api/customers'
import notifications from '@/utils/notifications'
import moment from 'moment'
import RelbaseDteDetailDialog from '@/components/relbase/RelbaseDteDetailDialog.vue'
import RelbaseIssuanceDialog from '@/components/relbase/RelbaseIssuanceDialog.vue'
import ShowWorkOrder from '@/components/production/ShowWorkOrder.vue'

export default {
  name: 'RelbaseDocumentsList',
  components: { RelbaseDteDetailDialog, RelbaseIssuanceDialog, ShowWorkOrder },
  data() {
    return {
      loading: false,
      loadingCustomers: false,
      documents: [],
      totalDocuments: 0,
      search: '',
      timer: null,
      customerSearch: null,
      customerTimer: null,
      customers: [],
      options: {},
      showDetail: false,
      selectedDoc: null,
      // Story 4.3: anulación
      showAnularConfirm: false,
      anularDoc: null,
      anulando: false,
      // Story 4.3: Nota de Crédito
      showNCDialog: false,
      ncReferenceDoc: null,
      // Story 4.5: Nuevo Documento Generalizado
      showNewDocumentDialog: false,
      newDocType: 33,
      filter: {
        date_emited_after: moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('YYYY-MM-DD'),
        date_emited_before: '',
        doc_type: '',
        customer: '',
      },
      docTypes: [
        { value: 33, text: 'Factura Electrónica' },
        { value: 39, text: 'Boleta Electrónica' },
        { value: 52, text: 'Guía de Despacho' },
        { value: 61, text: 'Nota de Crédito' },
      ],
      headers: [
        { text: 'Folio / Tipo', value: 'folio', class: 'table-header', width: '200px' },
        { text: 'Fecha Emisión', value: 'date_emited', class: 'table-header', width: '140px' },
        { text: 'Vencimiento', value: 'end_date', class: 'table-header', width: '130px', sortable: false },
        { text: 'Cliente', value: 'customer_name', class: 'table-header', sortable: false },
        { text: 'Total Bruto', value: 'total_bruto', class: 'table-header', align: 'end', width: '150px' },
        { text: 'OT Origen', value: 'work_order_id', class: 'table-header', sortable: false, width: '130px' },
        { text: '', value: 'actions', class: 'table-header', sortable: false, width: '60px', align: 'end' },
      ],
    }
  },
  watch: {
    customerSearch(val) {
      if (this.customerTimer) clearTimeout(this.customerTimer)
      this.customerTimer = setTimeout(() => this.searchCustomers(val), 400)
    },
  },
  methods: {
    docTypeColor(docType) {
      if (docType === 33) return 'primary'
      if (docType === 39) return 'success'
      if (docType === 52) return 'warning'
      if (docType === 61) return 'error'
      return 'grey'
    },
    openDetail(item) {
      this.selectedDoc = item
      this.showDetail = true
    },
    onFilterChange() {
      this.options.page = 1
      this.fetch()
    },
    searchTimeOut() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.options.page = 1
        this.fetch()
      }, 800)
    },
    async searchCustomers(query) {
      if (!query || query.length < 2) return
      this.loadingCustomers = true
      try {
        const res = await fetchCustomers('', 1, 20, query)
        this.customers = res.results || res
      } catch {
        // silencioso
      } finally {
        this.loadingCustomers = false
      }
    },
    // Story 4.3: Anulación
    confirmAnular(item) {
      this.anularDoc = item
      this.showAnularConfirm = true
    },
    async executeAnular() {
      if (!this.anularDoc) return
      this.showAnularConfirm = false
      this.anulando = true
      try {
        await anularRelbaseDTE(this.anularDoc.id)
        notifications.showSuccess(`Documento Folio #${this.anularDoc.folio} anulado exitosamente.`)
        this.fetch()
      } catch (e) {
        const msg = e?.response?.data?.message || 'Error al anular el documento en Relbase.'
        notifications.showError(msg)
      } finally {
        this.anulando = false
        this.anularDoc = null
      }
    },
    // Story 4.3: Nota de Crédito referenciada
    openNCDialog(item) {
      this.ncReferenceDoc = {
        id: item.id,
        folio: item.folio,
        doc_type: item.doc_type,
        doc_type_display: item.doc_type_display,
        date_emited: item.date_emited,
        customer_name: item.customer_name,
      }
      this.showNCDialog = true
    },
    onNCIssued(payload) {
      this.showNCDialog = false
      notifications.showSuccess(`Nota de Crédito emitida — Folio #${payload && payload.folio ? payload.folio : ''}`)
      this.fetch()
      this.openIssuedDocument(payload)
    },
    // Story 4.5: Nuevo Documento — Modo Generalizado
    openNewDocumentDialog() {
      this.$store.dispatch('relbase/resetDraft')
      this.newDocType = 33
      this.showNewDocumentDialog = true
    },
    onNewDocumentIssued(payload) {
      this.showNewDocumentDialog = false
      notifications.showSuccess(`Documento emitido — Folio #${payload && payload.folio ? payload.folio : ''}`)
      this.fetch()
      this.openIssuedDocument(payload)
    },
    openIssuedDocument(payload) {
      const id = payload && (payload.document_id || payload.id)
      if (!id) return
      this.selectedDoc = {
        id,
        folio: payload.folio,
        doc_type_display: payload.doc_type_display || 'Documento',
      }
      this.$nextTick(() => {
        this.showDetail = true
      })
    },

    async fetch() {
      this.loading = true
      const { sortBy, sortDesc, page, itemsPerPage } = this.options

      let ordering = '-created_at'
      if (sortBy && sortBy.length) {
        ordering = sortDesc && sortDesc[0] ? `-${sortBy[0]}` : sortBy[0]
      }

      const params = {
        page: page || 1,
        page_size: itemsPerPage || 20,
        ordering,
        search: this.search || undefined,
        date_emited_after: this.filter.date_emited_after || undefined,
        date_emited_before: this.filter.date_emited_before || undefined,
        doc_type: this.filter.doc_type || undefined,
        customer: this.filter.customer || undefined,
      }

      try {
        const res = await fetchRelbaseDocuments(params)
        this.totalDocuments = res.count
        this.documents = res.results
      } catch {
        notifications.showError('Error cargando documentos Relbase')
      } finally {
        this.loading = false
      }
    },
  },
  mounted() {
    this.fetch()
  },
}
</script>

<style scoped>
.modern-master-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -0.5px; }
.opacity-40 { opacity: 0.4; }
.tabular-nums { font-variant-numeric: tabular-nums; }

.modern-input >>> fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-search-field { width: 280px; }
.modern-search-field >>> fieldset { border-radius: 24px !important; border-color: #e0e4e8 !important; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 56px !important; text-transform: uppercase; font-size: 0.75rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; }
.modern-table >>> td { height: 72px !important; border-bottom: 1px solid #f0f3f5 !important; }
.modern-table >>> tr:hover { background-color: #f4f7f9 !important; }
</style>
